import { useEffect, useMemo, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $countryProvincesStore, $countriesStore, $currenciesStore, $recipientUpdateStore } from '@lib/store';
import { useDebounce } from '@shared/hooks';
import { IbanUtils, PaymentUtils, ValidateUtils } from '@shared/utils';
import { PaymentApi, TransferRecipientAccountTypeEnum, TransferRecipientTypeEnum, VopStatus, type RecipientResponse } from '@shared/api';
import { AccountFormData, AddressFormData, CompanyFormData, PersonalFormData, RecipientVerificationModal, VerifiedRecipientModal } from '@widgets';
import { BaseInput, Button, Modal, Select, Spinner } from '@shared/widgets';

const TYPE_OPTIONS = Object.entries(TransferRecipientTypeEnum).map(([, value]) => ({ label: value, value }));
const ACCOUNT_TYPE_OPTIONS = Object.entries(TransferRecipientAccountTypeEnum).map(([, value]) => ({ label: value, value }));

type RecipientFormData = { type: TransferRecipientTypeEnum } & (PersonalFormData | CompanyFormData) & AddressFormData & AccountFormData;

const getDefaultValues = (data: RecipientResponse): RecipientFormData => ({
  type: data.type || '',
  firstName: data.firstName || '',
  lastName: data.lastName || '',
  idNumber: data.idNumber || '',
  companyName: data.companyName || '',
  accountNumber: data.accountNumber || '',
  accountType: data.accountType || '',
  iban: data.iban || '',
  bankName: data.bankName || '',
  bankProvince: data.bankProvince || '',
  bankAddress: data.bankAddress || '',
  bankCity: data.bankCity || '',
  branchCode: data.branchCode || '',
  bankCountry: data.bankCountryCode || '',
  bic: data.bic || '',
  companyCountry: data.countryCode || '',
  currency: data.currency || '',
  email: data.email || '',
  address: data.address || '',
  postCode: data.postCode || '',
  city: data.city || '',
  province: data.province || '',
});

interface EditRecipientModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: RecipientResponse;
  onSubmit?: () => void;
}

export const EditRecipientModal = (props: EditRecipientModalProps) => {
  const countryProvincesStore = useUnit($countryProvincesStore.store);
  const countryStore = useUnit($countriesStore.store);
  const currenciesStore = useUnit($currenciesStore.store);
  const recipientUpdateStore = useUnit($recipientUpdateStore.store);

  const [ibanError, setIbanError] = useState<string>('');
  const isIbanEditedByUserRef = useRef(false);

  const { control, formState, handleSubmit, watch, getValues, setValue, setError, clearErrors, reset } = useForm<RecipientFormData>({
    defaultValues: getDefaultValues(props.data),
  });
  const [type, accountNumber, iban, bankCountry, countryCode] = watch(['type', 'accountNumber', 'iban', 'bankCountry', 'companyCountry']);

  const { t } = useTranslation('recipient');

  useEffect(() => {
    Promise.allSettled([
      $countryProvincesStore.events.getCountryProvinces(),
      $countriesStore.events.getCountries(),
      $currenciesStore.events.getCurrencies(),
    ]);
  }, []);

  useEffect(() => {
    if (props.isOpen) {
      isIbanEditedByUserRef.current = false;
      setIbanError('');
    }
  }, [props.isOpen, props.data.id]);

  const debouncedIban = useDebounce(iban);
  useEffect(() => {
    if (!props.isOpen || !debouncedIban || debouncedIban.includes(' ') || !isIbanEditedByUserRef.current) return;
    (async () => {
      setValue('bankCountry', '');
      setValue('bankProvince', '');
      setValue('bic', '');
      setValue('bankName', '');
      setIbanError('');

      const ibanValidation = (await PaymentApi.validateIban(debouncedIban)).data!;

      if (ibanValidation.isValid) {
        const countryCode = IbanUtils.getCountry(debouncedIban);
        const selectedCountry = countryStore.data.find((e) => e.twoLetterCode === countryCode);

        if (selectedCountry?.twoLetterCode !== getValues('bankCountry'))
          setValue('bankCountry', selectedCountry?.twoLetterCode || '', { shouldValidate: true });
        if (ibanValidation.bic !== undefined) setValue('bic', `${ibanValidation.bic}${ibanValidation.branchBic || ''}`, { shouldValidate: true });
        if (ibanValidation.bankName) setValue('bankName', ibanValidation.bankName, { shouldValidate: true });

        clearErrors('iban');
      } else {
        setError('iban', { message: t('errors.form.iban.invalidFormat', { ns: 'common' }) });
        setIbanError(t('errors.form.iban.invalidFormat', { ns: 'common' }));
      }
    })();
  }, [debouncedIban, props.isOpen]);

  const closeHandler = () => {
    isIbanEditedByUserRef.current = false;
    setIbanError('');
    props.onClose();
    reset(getDefaultValues(props.data));
  };
  const submitHandler = async (data: RecipientFormData) => {
    await PaymentUtils.updateRecipient({ ...data, id: props.data.id });
    props.onClose();
    props.onSubmit?.();
  };
  const submitWithVopConfirmationHandler = async () => {
    await PaymentUtils.updateRecipient({ ...getValues(), id: props.data.id, vopForceUpdate: true });
    props.onClose();
    props.onSubmit?.();
  };

  const isIndividual = type === TransferRecipientTypeEnum.Individual;
  const isBusiness = type === TransferRecipientTypeEnum.Business;
  const countryOptions = useMemo(
    () => countryStore.data.map((country) => ({ label: country.name, value: country.twoLetterCode })),
    [countryStore.data],
  );
  const bankProvinceOptions = useMemo(
    () =>
      bankCountry in countryProvincesStore.data
        ? countryProvincesStore.data[bankCountry].map((countryProvince) => ({ label: countryProvince, value: countryProvince }))
        : [],
    [countryProvincesStore.data, bankCountry],
  );
  const provinceOptions = useMemo(
    () =>
      countryCode in countryProvincesStore.data
        ? countryProvincesStore.data[countryCode].map((countryProvince) => ({ label: countryProvince, value: countryProvince }))
        : [],
    [countryProvincesStore.data, countryCode],
  );
  return (
    <>
      <Spinner show={recipientUpdateStore.loading || formState.isSubmitting} position />
      <Modal
        isOpen={props.isOpen && !recipientUpdateStore.error?.errorCode}
        onClose={closeHandler}
        title={t('details.modals.editRecipient.title')}
        width='468px'
      >
        <form className='flex flex-col gap-4' noValidate onSubmit={handleSubmit(submitHandler)}>
          <Controller
            name='type'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field, fieldState: { error } }) => (
              <Select
                label={t('details.fields.type')}
                nullValue={{ isEnable: false, text: t('details.fields.type') }}
                items={TYPE_OPTIONS.map((option) => ({ ...option, label: t(`recipientType.${option.label.toLowerCase()}`, { ns: 'common' }) }))}
                required
                error={error?.message}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          {isIndividual && (
            <Controller
              name='firstName'
              control={control}
              rules={{
                required: t('errors.form.required', { ns: 'common' }),
                maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
                pattern: {
                  value: ValidateUtils.alphaNumericRegex,
                  message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('details.fields.name') }),
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <BaseInput {...field} label={t('details.fields.name')} placeholder={t('details.fields.name')} required error={error?.message} />
              )}
            />
          )}
          {isIndividual && (
            <Controller
              name='lastName'
              control={control}
              rules={{
                required: t('errors.form.required', { ns: 'common' }),
                maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
                pattern: {
                  value: ValidateUtils.alphaNumericRegex,
                  message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('details.fields.lastName') }),
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <BaseInput
                  {...field}
                  label={t('details.fields.lastName')}
                  placeholder={t('details.fields.lastName')}
                  required
                  error={error?.message}
                />
              )}
            />
          )}
          {isIndividual && (
            <Controller
              name='idNumber'
              control={control}
              rules={{
                maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
                pattern: {
                  value: ValidateUtils.alphaNumericRegex,
                  message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('details.fields.idNumber') }),
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <BaseInput {...field} label={t('details.fields.idNumber')} placeholder={t('details.fields.idNumber')} error={error?.message} />
              )}
            />
          )}
          {isBusiness && (
            <Controller
              name='companyName'
              control={control}
              rules={{
                required: t('errors.form.required', { ns: 'common' }),
                maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
                pattern: {
                  value: ValidateUtils.alphaNumericRegex,
                  message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('details.fields.companyName') }),
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <BaseInput
                  {...field}
                  label={t('details.fields.companyName')}
                  placeholder={t('details.fields.companyName')}
                  required
                  error={error?.message}
                />
              )}
            />
          )}
          <Controller
            name='accountNumber'
            control={control}
            rules={{
              required: !iban ? t('errors.form.required', { ns: 'common' }) : false,
              maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
              pattern: {
                value: ValidateUtils.alphaNumericRegex,
                message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('details.fields.accountNumber') }),
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput
                {...field}
                label={t('details.fields.accountNumber')}
                placeholder={t('details.fields.accountNumber')}
                required
                error={error?.message}
              />
            )}
          />
          <Controller
            name='accountType'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field, fieldState: { error } }) => (
              <Select
                label={t('details.fields.accountType')}
                nullValue={{ isEnable: false, text: t('details.fields.accountType') }}
                items={ACCOUNT_TYPE_OPTIONS.map((option) => ({
                  ...option,
                  label: t(`transferRecipientAccountType.${option.label.toLowerCase()}`, { ns: 'common' }),
                }))}
                required
                error={error?.message}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name='iban'
            control={control}
            rules={{
              required: !accountNumber ? t('errors.form.required', { ns: 'common' }) : false,
              pattern: { value: ValidateUtils.noSpacesRegex, message: t('errors.form.noSpaces', { ns: 'common' }) },
              validate: () => {
                if (ibanError) return ibanError;
                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput
                {...field}
                label={t('details.fields.ibanNumber')}
                placeholder={t('details.fields.ibanNumber')}
                noSpaces
                required={!accountNumber}
                error={error?.message}
                onChange={(value) => {
                  isIbanEditedByUserRef.current = true;
                  if (!value) setIbanError('');
                  field.onChange(value);
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  isIbanEditedByUserRef.current = true;
                  field.onChange(e.clipboardData.getData('text').replace(/\s+/g, '').trim());
                }}
              />
            )}
          />
          <Controller
            name='bankName'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
              pattern: {
                value: ValidateUtils.alphaNumericRegex,
                message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('details.fields.bankName') }),
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput {...field} label={t('details.fields.bankName')} placeholder={t('details.fields.bankName')} required error={error?.message} />
            )}
          />
          <Controller
            name='bankProvince'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                enableSearch
                label={t('details.fields.bankProvince')}
                isLoading={countryProvincesStore.loading}
                items={bankProvinceOptions}
                nullValue={{ isEnable: true, text: t('details.fields.bankProvince') }}
                error={error?.message}
                value={field.value}
                onChange={(value) => field.onChange(value ? value : '')}
              />
            )}
          />
          <Controller
            name='bankAddress'
            control={control}
            rules={{
              maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
              pattern: {
                value: ValidateUtils.alphaNumericRegex,
                message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('details.fields.bankAddress') }),
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput {...field} label={t('details.fields.bankAddress')} placeholder={t('details.fields.bankAddress')} error={error?.message} />
            )}
          />
          <Controller
            name='bankCity'
            control={control}
            rules={{
              maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
              pattern: {
                value: ValidateUtils.alphaNumericRegex,
                message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('details.fields.bankCity') }),
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput {...field} label={t('details.fields.bankCity')} placeholder={t('details.fields.bankCity')} error={error?.message} />
            )}
          />
          <Controller
            name='branchCode'
            control={control}
            rules={{
              maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
              pattern: {
                value: ValidateUtils.alphaNumericRegex,
                message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('details.fields.branchCode') }),
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput {...field} label={t('details.fields.branchCode')} placeholder={t('details.fields.branchCode')} error={error?.message} />
            )}
          />
          <Controller
            name='bankCountry'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                enableSearch
                label={t('details.fields.bankCountry')}
                isLoading={countryStore.loading}
                items={countryOptions}
                nullValue={{ isEnable: false, text: t('details.fields.bankCountry') }}
                error={error?.message}
                value={field.value}
                onChange={(value) => (field.onChange(value), setValue('bankProvince', ''))}
              />
            )}
          />
          <Controller
            name='bic'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
              pattern: { value: ValidateUtils.bicCodeRegex, message: t('errors.form.bicSwift.invalidFormat', { ns: 'common' }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput
                {...field}
                label={t('details.fields.bicSwiftCode')}
                placeholder={t('details.fields.bicSwiftCode')}
                required
                error={error?.message}
              />
            )}
          />
          <Controller
            name='companyCountry'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field, fieldState: { error } }) => (
              <Select
                enableSearch
                label={t(
                  `details.fields.${props.data.type === TransferRecipientTypeEnum.Individual ? 'beneficiaryResidentialCountry' : 'incorporationCountry'}`,
                )}
                items={countryOptions}
                nullValue={{
                  isEnable: false,
                  text: t(
                    `details.fields.${props.data.type === TransferRecipientTypeEnum.Individual ? 'beneficiaryResidentialCountry' : 'incorporationCountry'}`,
                  ),
                }}
                required
                error={error?.message}
                value={field.value}
                onChange={(value) => (field.onChange(value), setValue('province', ''))}
              />
            )}
          />
          <Controller
            name='currency'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field, fieldState: { error } }) => (
              <Select
                enableSearch
                label={t('details.fields.currency')}
                isLoading={currenciesStore.loading}
                items={currenciesStore.data}
                nullValue={{ isEnable: false, text: t('details.fields.currency') }}
                required
                error={error?.message}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name='email'
            control={control}
            rules={{ pattern: { value: ValidateUtils.emailRegex, message: t('errors.form.email.invalidFormat', { ns: 'common' }) } }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput {...field} label={t('details.fields.email')} placeholder={t('details.fields.email')} error={error?.message} />
            )}
          />
          <Controller
            name='address'
            control={control}
            rules={{
              maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
              pattern: {
                value: ValidateUtils.alphaNumericRegex,
                message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('details.fields.address') }),
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput {...field} label={t('details.fields.address')} placeholder={t('details.fields.address')} error={error?.message} />
            )}
          />
          <Controller
            name='postCode'
            control={control}
            rules={{
              maxLength: { value: 10, message: t('errors.form.maxCharacters', { ns: 'common', max: 10 }) },
              pattern: {
                value: ValidateUtils.alphaNumericRegex,
                message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('details.fields.postCode') }),
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput {...field} label={t('details.fields.postCode')} placeholder={t('details.fields.postCode')} error={error?.message} />
            )}
          />
          <Controller
            name='city'
            control={control}
            rules={{
              maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
              pattern: {
                value: ValidateUtils.alphaNumericRegex,
                message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('details.fields.townCity') }),
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput {...field} label={t('details.fields.townCity')} placeholder={t('details.fields.townCity')} error={error?.message} />
            )}
          />
          <Controller
            name='province'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                enableSearch
                label={t('details.fields.provinceRegion')}
                isLoading={countryProvincesStore.loading}
                items={provinceOptions}
                nullValue={{ isEnable: true, text: t('details.fields.provinceRegion') }}
                error={error?.message}
                value={field.value}
                onChange={(value) => field.onChange(value ? value : '')}
              />
            )}
          />
          <Button className='mt-5' fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
            {t('buttons.save', { ns: 'common' })}
          </Button>
        </form>
      </Modal>
      <VerifiedRecipientModal
        isOpen={recipientUpdateStore.data?.vopStatus === VopStatus.Verified}
        description='A recipient was updated'
        onClose={() => $recipientUpdateStore.events.clear()}
      />
      <RecipientVerificationModal
        isOpen={!!recipientUpdateStore.error?.errorCode}
        onClose={() => $recipientUpdateStore.events.clear()}
        type='updateRecipient'
        error={recipientUpdateStore.error}
        onSubmit={submitWithVopConfirmationHandler}
      />
    </>
  );
};
